// hooks
import { type Pagination, useCommonMutation, useCommonQuery } from 'hooks';

// modules
import type {
  ReqSafetyList,
  ResSafetyItem,
  SafetyDetailProps,
  CreateSafetyProps,
  UpdateSafetyStatusProps,
  SafetyAttendanceProps,
  ReqSafetyDetailProps,
  ReqCreateSafetyByManual
} from '../model/safety.model';
import type { BaseApiResponse, CommonQueryOptions, ResultError } from 'modules';

// apis
import { SafetyAPI } from '../apis/safety.api';

// keys
import { safetyKeys } from '../keys/safety.key';

export const safetyApi = new SafetyAPI();

/** 안전 교육 목록 조회 쿼리 훅 */
export const useGetSafetyList = ({
  data,
  options
}: {
  data: ReqSafetyList;
  options?: CommonQueryOptions<
    BaseApiResponse<unknown, ResSafetyItem>,
    ResultError,
    ResSafetyItem[]
  >;
}) => {
  const queryKeyFactory = (data: ReqSafetyList) => safetyKeys.GET_SAFETY_TRAININGS(data);

  const queryFn = async (data: ReqSafetyList, pagination: Pagination) => {
    const req = {
      ...data,
      page: pagination.current,
      size: pagination.pageSize
    };

    return safetyApi.getSafeties(req);
  };

  return useCommonQuery<
    ReqSafetyList,
    BaseApiResponse<unknown, ResSafetyItem>,
    ResultError,
    ResSafetyItem[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 안전 교육 상세 조회 쿼리 훅 */
export const useGetSafetyDetail = ({
  data,
  options
}: {
  data: ReqSafetyDetailProps;
  options?: CommonQueryOptions<
    BaseApiResponse<SafetyDetailProps, SafetyAttendanceProps>,
    ResultError,
    { attendaceList: SafetyAttendanceProps[]; detail: SafetyDetailProps }
  >;
}) => {
  const queryKeyFactory = (data: ReqSafetyDetailProps) =>
    safetyKeys.GET_SAFETY_TRAINING_DETAIL(data);

  const queryFn = async (data: ReqSafetyDetailProps, pagination: Pagination) => {
    const { safetyId, ...rest } = data;

    const req = {
      ...rest,
      page: pagination.current,
      size: pagination.pageSize
    };

    return await safetyApi.getSafetyDetail(safetyId, req);
  };

  return useCommonQuery<
    ReqSafetyDetailProps,
    BaseApiResponse<SafetyDetailProps, SafetyAttendanceProps>,
    ResultError,
    { attendaceList: SafetyAttendanceProps[]; detail: SafetyDetailProps }
  >({
    data,
    options: {
      ...options,
      select: (data) => ({ attendaceList: data.resultList, detail: data.resultObject })
    },
    queryKeyFactory,
    queryFn
  });
};

/** 안전 교육 등록 쿼리 훅 */
export const useCreateSafety = () => {
  return useCommonMutation<BaseApiResponse<unknown>, ResultError, CreateSafetyProps>({
    mutationKey: safetyKeys.SAFETY_ALL,
    mutationFn: async (data) => {
      const formData = new FormData();

      formData.append('safety', JSON.stringify(data));

      return safetyApi.createSafety(formData);
    },
    queryKeyToInvalidate: safetyKeys.SAFETY_ALL
  });
};

/** 안전 교육 수정 쿼리 훅 */
export const useModifySafety = () => {
  return useCommonMutation<BaseApiResponse<unknown>, ResultError, CreateSafetyProps>({
    mutationKey: safetyKeys.SAFETY_ALL,
    mutationFn: async (data) => {
      const formData = new FormData();

      formData.append('safety', JSON.stringify(data));

      return await safetyApi.modifySafety(formData);
    },
    queryKeyToInvalidate: safetyKeys.SAFETY_ALL
  });
};

/** 안전 교육 상태 업데이트 쿼리 훅 */
export const useUpdateSafetyStatus = () => {
  return useCommonMutation<BaseApiResponse, ResultError, UpdateSafetyStatusProps>({
    mutationKey: safetyKeys.SAFETY_ALL,
    mutationFn: async (data) => {
      return await safetyApi.updateSafetyStatus(data);
    },
    queryKeyToInvalidate: safetyKeys.SAFETY_ALL
  });
};

/** 안전 교육 상태 업데이트 쿼리 훅 */
export const useDeleteSafetyAttendace = () => {
  return useCommonMutation<BaseApiResponse, ResultError, string>({
    mutationKey: safetyKeys.SAFETY_ALL,
    mutationFn: async (data) => {
      return await safetyApi.deleteSafetyAttendance(data);
    },
    queryKeyToInvalidate: safetyKeys.SAFETY_ALL
  });
};

/** 안전 교육 수동 등록 쿼리 훅 */
export const useCreateSafetyByManual = () => {
  return useCommonMutation<BaseApiResponse, ResultError, ReqCreateSafetyByManual>({
    mutationKey: safetyKeys.SAFETY_ALL,
    mutationFn: async (data) => {
      return await safetyApi.createSafetyByManual(data);
    },
    queryKeyToInvalidate: safetyKeys.SAFETY_ALL
  });
};
